/**
 * abilityCapture.config — auto-screenshot-on-cast knobs (DJ ask 2026-06-12).
 *
 * When enabled (?abilityCapture=1, auto-injected on /abilitygrounds), every
 * successful LOCAL ability cast extracts a burst of world frames from the
 * Pixi stage and uploads them to the game server it is connected to
 * (`POST /api/diag/ability-captures/frame`), where they're queryable by
 * player/ability and auto-pruned (server-side retention, default 60 min).
 *
 * Sibling of the other modular feel/diag configs (remote-vfx.config,
 * statusAura.config, ...). Consumer: src/game/diagnostics/AbilityCastCapture.ts.
 */
import { bootSearchParams } from '../boot/bootUrl';
/** What region each frame covers. 'viewport' = full screen; 'player' =
 *  a focus.sizePx square around the caster; 'cast' = the union of caster +
 *  target squares (projectile path stays in frame). */
export type CaptureFocusMode = 'viewport' | 'player' | 'cast';

export const ABILITY_CAPTURE_CONFIG = {
  /** FALLBACK frame offsets when the ability has no timing data —
   *  the real schedule is derived per cast from windUpMs / projectile
   *  flight / durationMs (captureSchedule.ts). */
  frameOffsetsMs: [0, 250, 600, 1200],

  /** Knobs for the derived per-cast schedule (captureSchedule.ts). */
  schedule: {
    /** Frame just after the windup completes (muzzle moment). */
    releasePadMs: 80,
    /** Frame just after estimated impact (hit VFX + FCT on screen). */
    impactPadMs: 120,
    /** Aftermath frame — lingering VFX, status auras, FCT fade. */
    aftermathMs: 500,
    /** Only add a mid-flight frame when travel is at least this long. */
    midFlightMinMs: 200,
    /** Assumed caster→target distance when no live target resolves (px). */
    defaultTargetDistancePx: 300,
    /** Sanity cap on estimated projectile flight (ms). */
    flightCapMs: 1500,
    /** Mid-duration peek cap for long buffs/auras (ms after release). */
    durationPeekCapMs: 1500,
    /** Minimum gap between frames after dedupe (ms). */
    minGapMs: 120,
    /** Hard cap on any offset (ms). */
    maxMs: 4000,
    /** Hard cap on frames per cast. */
    maxFrames: 6,
    /** Phase labeling: offsets within this window past impact = 'impact'. */
    impactWindowMs: 400,

    // --- Channel body-spread (A9 / K12) ------------------------------------
    // A single durationPeek anchor clamps a long CHANNEL to frames inside its
    // head — drain_soul (a 15s channel: windUpMs 15000) sampled only ~0ms.
    // For channels, sample fixed fractions across the channel body instead.
    /** Fractions of the channel body to sample (isChanneled casts only). */
    channelSampleFractions: [0.25, 0.5, 0.75, 0.95],
    /** Offset cap for channel-body frames (ms). Larger than maxMs so a long
     *  channel still spreads across a real window instead of collapsing every
     *  body anchor onto the maxMs tail. */
    channelMaxMs: 6000,

    // --- Event-triggered late frames (A1 / VQA-08) -------------------------
    // The a-priori schedule can't predict a beat that fires on a runtime timer
    // (ravager whirl ~1000ms) or a server-confirmed kill (/pve dissolve
    // ~+2.6s, past the burst end). captureCast polls VisualLifecycleStore and
    // photographs a beat WHEN it actually fires, keyed on the beat timestamp.
    /** Lifecycle beat types that earn a late frame when they fire. */
    eventBeatTypes: ['hit_vfx', 'death_vfx', 'status_vfx', 'aoe_vfx', 'ground_impact_vfx', 'reaction_vfx'],
    /** Delay from observing a beat to capturing its frame (ms). */
    eventPadMs: 40,
    /** Longest a beat may fire after cast and still be photographed (ms).
     *  Covers the /pve dissolve at ~+2.6s. Dictated cross-agent constant. */
    eventMaxMs: 4000,
    /** Max event-triggered frames per cast (each ≥ minGapMs apart). */
    maxEventFrames: 4,
    /** How many recent lifecycle rows each poll examines. */
    eventScanN: 12,
  },

  /** Default framing — cast-focused crops read far better in QA review
   *  than full 1280×720 frames (most of which is empty arena). URL
   *  overrides: ?captureFocus=viewport|player|cast & ?captureSize=500. */
  focus: { mode: 'cast' as CaptureFocusMode, sizePx: 500 },

  /** JPEG quality (0..1) — captures are evidence, not art. */
  jpegQuality: 0.6,

  /** Extract resolution scalar — 0.5 halves both axes (~quarter the bytes).
   *  Full-res devicePixelRatio frames are wasteful for QA review. */
  resolution: 0.5,

  /** Resolution for FOCUSED (cropped) frames — the region is already small,
   *  so keep full detail (a 500px crop at 0.5 would ship as a mushy 250px). */
  focusResolution: 1,

  /** Min gap between captured casts. Spam-casting only records the first
   *  cast per window — protects the wire + server disk during rotations. */
  minIntervalMs: 1500,

  /** Max concurrently-running capture sequences (each is N frames). */
  maxConcurrentSequences: 2,
} as const;

/** ?abilityCapture=1 — explicit opt-in, same URL-gate style as ?testhooks=1.
 *  No preset/devConfig coupling: capture is a per-tab QA decision. */
export function isAbilityCaptureEnabled(): boolean {
  try {
    if (typeof window === 'undefined') return false;
    return bootSearchParams().get('abilityCapture') === '1';
  } catch {
    return false;
  }
}

/** Resolve framing: ?captureFocus= / ?captureSize= override config defaults. */
export function getAbilityCaptureFocus(): { mode: CaptureFocusMode; sizePx: number } {
  const fallback = { ...ABILITY_CAPTURE_CONFIG.focus };
  try {
    if (typeof window === 'undefined') return fallback;
    const sp = bootSearchParams();
    const m = sp.get('captureFocus');
    const mode: CaptureFocusMode =
      m === 'viewport' || m === 'player' || m === 'cast' ? m : fallback.mode;
    const sizePx = Number(sp.get('captureSize')) || fallback.sizePx;
    return { mode, sizePx };
  } catch {
    return fallback;
  }
}
